import { FOUNDATION_SUITS, isRed, type Card, type GameState, type Suit } from "./types";

/**
 * Regles pures du Klondike : validation des empilements, sans aucune notion
 * d'etat global ni d'animation. Tout est fonction pure (Card, Card) -> bool,
 * pour etre trivialement testable.
 */

/** Empilement dans le tableau : ordre decroissant, couleurs alternees (rouge/noir). */
export function canStackOnTableauCard(moving: Card, target: Card): boolean {
  return target.faceUp && moving.rank === target.rank - 1 && isRed(moving.suit) !== isRed(target.suit);
}

/** Une colonne vide n'accepte qu'un Roi (seul ou en tete de sequence). */
export function canMoveToTableau(moving: Card, targetPile: readonly Card[]): boolean {
  const top = targetPile[targetPile.length - 1];
  if (!top) return moving.rank === 13;
  return canStackOnTableauCard(moving, top);
}

/** Fondation : meme enseigne, de l'As au Roi dans l'ordre croissant. */
export function canMoveToFoundation(moving: Card, foundation: readonly Card[], suit: Suit): boolean {
  if (moving.suit !== suit) return false;
  const top = foundation[foundation.length - 1];
  if (!top) return moving.rank === 1;
  return moving.rank === top.rank + 1;
}

/** Index de la fondation d'une enseigne dans GameState.foundations. */
export function foundationIndex(suit: Suit): number {
  return FOUNDATION_SUITS.indexOf(suit);
}

/**
 * Une sequence du tableau est deplacable d'un seul geste a partir de `index`
 * si toutes les cartes a partir de la sont face visible. (Par construction du
 * Klondike, toute suite de cartes face visible d'une colonne est deja ordonnee
 * en alternance decroissante : on ne peut pas y empiler autre chose.)
 */
export function canPickUpRun(pile: readonly Card[], index: number): boolean {
  if (index < 0 || index >= pile.length) return false;
  for (let i = index; i < pile.length; i++) {
    if (!pile[i]!.faceUp) return false;
  }
  return true;
}

/** Victoire : les 4 fondations completes (13 cartes chacune). */
export function isWon(state: GameState): boolean {
  return state.foundations.every((pile) => pile.length === 13);
}

/**
 * Auto-complete disponible : plus aucune carte cachee nulle part (pioche et
 * defausse vides, tout le tableau face visible) — la victoire est alors
 * mathematiquement garantie, il ne reste que des coups triviaux vers les
 * fondations (cahier des charges §6).
 */
export function canAutoComplete(state: GameState): boolean {
  if (isWon(state)) return false;
  if (state.stock.length > 0 || state.waste.length > 0) return false;
  return state.tableau.every((pile) => pile.every((card) => card.faceUp));
}
